<template>
<div class="chapter">
<div class="inner_page">
<h2 class="chaptitleo"><a id="page_6"><span class="bold">Knitting know-how</span></a></h2><a id="page_6">
<p class="h3"><span class="bold">Yarn</span></p>
<p class="noindent1">The animals are knitted with fleecy yarn in DK (8-ply/light worsted) weight. You don't need much yarn to create each toy. Some of them require less than 5m (5.5yd).</p>
<p class="noindent1">Most fleecy yarn comes in two different weights; DK (8-ply/light worsted) and chunky (bulky). Even though two types of yarn may be categorized as the same weight, their thickness and yardage may differ depending on the brand. You may prefer one brand to another, so try several to find your favourite. If you want to make larger animals, you can use chunky (bulky) weight yarn and larger needles, without having to change the patterns. Most of the animals are knitted using yarn with a yardage of 25g per 83-85m (91-93yd).</p>
<p class="noindent1">Tiny amounts of ordinary DK (8-ply/light worsted) yarn and 4-ply (fingering) yarn are used for the smaller body parts, such as ears and feet, and also embroidering features, such as noses and mouths. If you need to buy yarn, tapestry yarn is a good choice because it comes in small amounts and a huge variety of colours.</p>
<p class="h4"><span class="bold">Stuffing</span></p>
<p class="noindent1">I have used polyester toy stuffing, which is readily available from most craft shops and online craft stores.</p>
<p class="h4"><span class="bold">Beads</span></p>
<p class="noindent1">4-6mm (1⁄6–¼in) beads are used as eyes for a few of the projects. I prefer to use beads with threading holes and attach them after the head is stuffed; however, if you are making an animal for a child, you may prefer to use safety eyes. If you are using this type, attach them before stuffing the head. Alternatively, you can embroider French knots with DK (8-ply/light worsted) yarn.</p>
<p class="h4"><span class="bold">Knitting needles</span></p>
<p class="noindent1">Throughout the book I used 3.5mm (UK 9 or 10, US 4) needles for the fleecy DK (8-ply/light worsted) yarn, and 3mm (UK 11, US 2 or 3) needles for regular DK (8-ply/light worsted) yarn. I always use bamboo needles as they are less slippery than metal ones and keep your stitches neat and even. Your knitting tension (gauge) needs to be fairly tight, so that when toys are sewn up, the stuffing is not visible through the stitches. If you struggle with knitting fleecy yarn on these needle sizes, experiment with larger needles. Some knitters knit more tightly than others and the tension (gauge) also differs depending on the yarn you use. I have given a general tension (gauge) guide below, but tensions are</p>
<p class="noindent1">not specified for any of the projects, as the size of the finished items doesn't really matter.</p>
<p class="h4"><span class="bold">Sewing your work together</span></p>
<p class="noindent1">I recommend that you use a chenille or tapestry needle with a sharp point, as it is easier to work through your tightly knitted toys than using a blunt-ended needle. You can also use the same needle for embroidering features on the toys. Your toys will be sewn up using the same yarn that you knitted them with, so it is a good idea to make a habit of leaving fairly long yarn ends when you cast on and cast off. Join the seams using mattress stitch with right sides facing outwards.</p>
<p class="h4"><span class="bold">Stuffing the toys</span></p>
<p class="noindent1">These knitted pieces can be quite stretchy. Stuff them relatively lightly to keep the finished toys nice and soft.</p>
<p class="h4"><span class="bold">Shaping</span></p>
<p class="noindent1">It is not for all projects, but I find some of the faces look more attractive with an indentation for the eyes. With the fleecy yarn used for the head, work a gathering thread over the eyeline and pull gently to shape it.</p>
<p class="h4"><span class="bold">Tension (gauge)</span></p>
<p class="noindent1">I worked to a tension of 22 stitches x 26 rows = 10cm (4in) measured over stocking stitch (US stockinette stitch); however, it is not essential to stick to this.</p>
<p class="h3"><span class="bold">Other tools</span></p>
<p class="h4"><span class="bold">Wooden chopstick</span></p>
<p class="noindent1">A simple but incredibly effective tool, a chopstick is by far the best instrument for pushing stuffing into your toys. If you don't have one, you could use a large knitting needle or a pencil.</p>
<p class="h4"><span class="bold">Scissors</span></p>
<p class="noindent1">A pair of sharp scissors is essential for trimming yarn ends when sewing up your projects.</p>
<p class="h4"><span class="bold">Needle threader</span></p>
<p class="noindent1">This is not essential but I find it useful to help thread yarn on to my needle when I am sewing up projects.</p>
<p class="h4"><span class="bold">Row counter</span></p>
<p class="noindent1">This can be useful to keep count of your rows when you are working with fleecy yarn, as it's sometimes difficult to see them.</p>
</a><div class="img_container"><a id="page_6">
</a><a id="page_7"><img alt="image" src="images/pg7-1.jpg"/>
</a></div><a id="page_7">
<p class="h3"><span class="bold">Techniques</span></p>
<p class="h4"><span class="bold">I-cord</span></p>
<p class="noindent1">I-cords are used for some of the body parts. Using DPN, cast on the required number of stitches. Do not turn. Slide the stitches to the opposite end of the needle, then knit the stitches again, taking the yarn firmly across the back of the work. Repeat to the desired length. Break yarn and thread through the stitches or cast off, as instructed in the pattern.</p>
<p class="h3"><span class="bold">Embroidery</span></p>
<p class="h4"><span class="bold">French knots</span></p>
<p class="noindent1">These are used for many of the figures' and animals' eyes. Thread your needle with yarn and bring it up from the back where you want to make a knot. Holding the needle firmly, wrap the yarn around it twice. Push the needle through to the back of the project, leaving the French knot on the surface.</p>
<div class="img_container">
<img alt="image" src="images/pg7-2.jpg"/>
</div>
<div class="box1">
<p class="boxh"><span class="bold">Abbreviations</span></p>
<p class="boxn1">The abbreviations listed below are the most frequently used terms in the book. Any special abbreviations in a pattern are explained on the relevant page.</p>
<table class="table">
<colgroup>
<col style="width: 25%"/>
<col style="width: 75%"/>
</colgroup>
<tbody><tr>
<td><span class="bold">beg</span></td>
<td>beginning</td>
</tr>
<tr>
<td><span class="bold">cm</span></td>
<td>centimetres</td>
</tr>
<tr>
<td><span class="bold">DK</span></td>
<td>double knitting</td>
</tr>
<tr>
<td><span class="bold">DPN</span></td>
<td>double-pointed needle(s)</td>
</tr>
<tr>
<td><span class="bold">g</span></td>
<td>grams</td>
</tr>
<tr>
<td><span class="bold">g st</span></td>
<td>garter stitch</td>
</tr>
<tr>
<td><span class="bold">inc</span></td>
<td>increase</td>
</tr>
<tr>
<td><span class="bold">in(s)</span></td>
<td>inch(es)</td>
</tr>
<tr>
<td><span class="bold">k</span></td>
<td>knit</td>
</tr>
<tr>
<td><span class="bold">kfb</span></td>
<td>knit into the front and back of the stitch, making one more stitch</td>
</tr>
<tr>
<td><span class="bold">k2tog</span></td>
<td>knit 2 stitches together</td>
</tr>
<tr>
<td><span class="bold">k3tog</span></td>
<td>knit 3 stitches together</td>
</tr>
<tr>
<td><span class="bold">m</span></td>
<td>metres</td>
</tr>
<tr>
<td><span class="bold">mm</span></td>
<td>millimetres</td>
</tr>
<tr>
<td><span class="bold">oz</span></td>
<td>ounces</td>
</tr>
<tr>
<td><span class="bold">p</span></td>
<td>purl</td>
</tr>
<tr>
<td><span class="bold">pfb</span></td>
<td>purl into the front and back of the stitch, making one more stitch</td>
</tr>
<tr>
<td><span class="bold">p2tog</span></td>
<td>purl 2 stitches together</td>
</tr>
<tr>
<td><span class="bold">rep</span></td>
<td>repeat</td>
</tr>
<tr>
<td><span class="bold">RS</span></td>
<td>right side(s)</td>
</tr>
<tr>
<td><span class="bold">skpo</span></td>
<td>slip 1, knit 1, pass the slipped stitch over</td>
</tr>
<tr>
<td><span class="bold">sl1</span></td>
<td>slip one stitch from the left-hand needle to the right-hand needle without working it</td>
</tr>
<tr>
<td><span class="bold">st(s)</span></td>
<td>stitch(es)</td>
</tr>
<tr>
<td><span class="bold">st st</span></td>
<td>stocking stitch (US stockinette stitch)</td>
</tr>
<tr>
<td><span class="bold">tog</span></td>
<td>together</td>
</tr>
<tr>
<td><span class="bold">WS</span></td>
<td>wrong sides(s)</td>
</tr>
<tr>
<td><span class="bold">yb</span></td>
<td>yarn back</td>
</tr>
<tr>
<td><span class="bold">yf</span></td>
<td>yarn forward</td>
</tr>
<tr>
<td><span class="bold">yd</span></td>
<td>yard(s)</td>
</tr>
</tbody></table>
</div>
</a>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// 07_Chapter01.vue?0965fd20